import React, { useState, useEffect, useMemo, useCallback } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Select,
  Input,
  InputNumber,
  Button,
  Radio,
  Alert,
  Skeleton,
  Space,
  Tag,
  Card,
  Collapse,
} from "antd";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FilterOutlined,
  ClearOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import moment from "moment-jalaali";
import DatePicker from "@general/datePicker";
import getWindowSize from "@utils/windowSize";
import AdminServicesTable from "./table";
import AdminServiceTableDetails from "./tableDetails";
import {
  SERVICE_TYPE_OPTIONS,
  SERVICE_STATUS_OPTIONS,
} from "@staticData/services";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

export const GET_ADMIN_USER_SERVICES = gql`
  query GetAdminUserServices(
    $paginateInput: PaginateInput!
    $searchInput: AdminServiceSearchInput
  ) {
    getAdminUserServicesCount(searchInput: $searchInput)
    getAdminUserServices(
      paginateInput: $paginateInput
      searchInput: $searchInput
    ) {
      id
      serviceType
      status
      serviceName
      amount
      createdAt
      startAt
      endAt
      description
      contract
      userId
      user {
        id
        name
        mobile
        userRole
      }
      productId
      product {
        id
        name
        category
      }
      packageName
      packageDays
      packageSessions
      meetingRoomName
      workAt
      startTime
      endTime
      capacity
      reserve {
        id
        startAt
        endAt
      }
    }
  }
`;

export const GET_USER_NAME = gql`
  query GetUserName($id: ID!) {
    getUser(id: $id) {
      id
      name
    }
  }
`;

function AdminUserServices() {
  const { result } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [filterUserId, setFilterUserId] = useState(
    result && result !== "all" ? result : null
  );
  const [resolvedUserName, setResolvedUserName] = useState(
    location.state?.userName || null
  );
  const [userName, setUserName] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const [textSearch, setTextSearch] = useState("");
  const [serviceType, setServiceType] = useState(null);
  const [status, setStatus] = useState(null);

  const [createdAtRange, setCreatedAtRange] = useState({
    from: null,
    to: null,
  });
  const [startAtRange, setStartAtRange] = useState({ from: null, to: null });
  const [endAtRange, setEndAtRange] = useState({ from: null, to: null });

  const [expiryShortcut, setExpiryShortcut] = useState("ALL"); // 'ALL' | '1' | '7' | '30' | 'CUSTOM'
  const [customExpiryDays, setCustomExpiryDays] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const [selectedService, setSelectedService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [windowDimensions, setWindowDimensions] = useState(getWindowSize());

  useEffect(() => {
    const handleResize = () => setWindowDimensions(getWindowSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (result && result !== "all") {
      setFilterUserId(result);
      if (location.state?.userName) {
        setResolvedUserName(location.state.userName);
      }
    } else {
      setFilterUserId(null);
      setResolvedUserName(null);
    }
    setCurrentPage(1);
  }, [result, location.state]);

  const [fetchAdminServices, { data, loading, error }] = useLazyQuery(
    GET_ADMIN_USER_SERVICES,
    {
      fetchPolicy: "network-only",
    }
  );

  const [fetchUserName] = useLazyQuery(GET_USER_NAME, {
    fetchPolicy: "cache-first",
    onCompleted(userData) {
      if (userData?.getUser?.name) {
        setResolvedUserName(userData.getUser.name);
      }
    },
  });

  useEffect(() => {
    if (filterUserId && data?.getAdminUserServices?.length) {
      const matched = data.getAdminUserServices.find(
        (s) => s.userId === filterUserId || s.user?.id === filterUserId
      );
      if (matched?.user?.name) {
        setResolvedUserName(matched.user.name);
      }
    }
  }, [filterUserId, data]);

  useEffect(() => {
    if (
      filterUserId &&
      !resolvedUserName &&
      !location.state?.userName &&
      data &&
      !loading &&
      data.getAdminUserServicesCount === 0
    ) {
      fetchUserName({ variables: { id: filterUserId } });
    }
  }, [filterUserId, resolvedUserName, location.state, data, loading, fetchUserName]);

  const searchInput = useMemo(() => {
    const input = {};

    if (filterUserId) {
      input.userId = [filterUserId];
    }

    if (userName && userName.trim()) {
      input.userName = userName.trim();
    }

    if (userMobile && userMobile.trim()) {
      input.userMobile = userMobile.trim();
    }

    if (textSearch && textSearch.trim()) {
      input.textSearch = textSearch.trim();
    }

    if (serviceType) {
      input.serviceType = [serviceType];
    }

    if (status) {
      input.status = [status];
    }

    if (createdAtRange.from && createdAtRange.to) {
      input.createdAt = [createdAtRange.from, createdAtRange.to];
    }

    if (startAtRange.from && startAtRange.to) {
      input.startAt = [startAtRange.from, startAtRange.to];
    }

    if (endAtRange.from && endAtRange.to) {
      input.endAt = [endAtRange.from, endAtRange.to];
    }

    if (expiryShortcut === "1") {
      input.expiringWithinDays = 1;
    } else if (expiryShortcut === "7") {
      input.expiringWithinDays = 7;
    } else if (expiryShortcut === "30") {
      input.expiringWithinDays = 30;
    } else if (expiryShortcut === "CUSTOM" && customExpiryDays > 0) {
      input.expiringWithinDays = customExpiryDays;
    }

    return input;
  }, [
    filterUserId,
    userName,
    userMobile,
    textSearch,
    serviceType,
    status,
    createdAtRange,
    startAtRange,
    endAtRange,
    expiryShortcut,
    customExpiryDays,
  ]);

  const loadData = useCallback(() => {
    fetchAdminServices({
      variables: {
        paginateInput: {
          page: currentPage,
          perPage: 10,
          sort: sortColumn,
          sortDir: sortDir,
        },
        searchInput,
      },
    });
  }, [fetchAdminServices, currentPage, sortColumn, sortDir, searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleClearAllFilters = () => {
    setUserName("");
    setUserMobile("");
    setTextSearch("");
    setServiceType(null);
    setStatus(null);
    setCreatedAtRange({ from: null, to: null });
    setStartAtRange({ from: null, to: null });
    setEndAtRange({ from: null, to: null });
    setExpiryShortcut("ALL");
    setCustomExpiryDays(null);
    setFilterUserId(null);
    setResolvedUserName(null);
    setCurrentPage(1);
    if (result && result !== "all") {
      navigate("/admin-services/all", { replace: true });
    }
  };

  const parseDateRange = (dateStrings) => {
    if (!dateStrings || !dateStrings[0] || !dateStrings[1]) {
      return { from: null, to: null };
    }
    const from = moment(dateStrings[0], "jYYYY/jMM/jDD")
      .startOf("day")
      .toDate()
      .toISOString();
    const to = moment(dateStrings[1], "jYYYY/jMM/jDD")
      .endOf("day")
      .toDate()
      .toISOString();
    return { from, to };
  };

  const handleSort = (sorter) => {
    setCurrentPage(1);
    if (!sorter || !sorter.order) {
      setSortColumn("createdAt");
      setSortDir("desc");
      return;
    }
    setSortColumn(sorter.columnKey || "createdAt");
    setSortDir(sorter.order === "ascend" ? "asc" : "desc");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenDetails = (service) => {
    setSelectedService(service);
    setDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDrawerOpen(false);
  };

  const servicesList = data?.getAdminUserServices || [];
  const totalCount = data?.getAdminUserServicesCount || 0;

  const displayName = useMemo(() => {
    if (resolvedUserName) return resolvedUserName;
    if (location.state?.userName) return location.state.userName;
    const found = servicesList.find(
      (s) => s.userId === filterUserId || s.user?.id === filterUserId
    );
    if (found?.user?.name) return found.user.name;
    return "کاربر نامشخص";
  }, [resolvedUserName, location.state, servicesList, filterUserId]);

  return (
    <div style={{ padding: "0 0 40px 0" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
             سرویس‌های کاربران
          </Title>
        </Col>
        <Col>
          <Text strong>مجموع رکوردها: </Text>
          <Tag color="blue">{totalCount}</Tag>
        </Col>
      </Row>

      {filterUserId && (
        <Alert
          message={
            <Space>
              <span>فیلتر فعال برای کاربر:</span>
              <Tag color="geekblue">{displayName}</Tag>
              <Button
                size="small"
                type="link"
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setFilterUserId(null);
                  setResolvedUserName(null);
                  navigate("/admin-services/all", { replace: true });
                }}
              >
                حذف فیلتر کاربر و نمایش همه
              </Button>
            </Space>
          }
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {error && (
        <Alert
          message="خطا در دریافت اطلاعات سرویس‌ها"
          description={error.message}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filter Section */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          borderRadius: 8,
          border: "1px solid #f0f0f0",
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Search
              placeholder="نام کاربر"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onSearch={() => setCurrentPage(1)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Search
              placeholder="شماره موبایل کاربر"
              value={userMobile}
              onChange={(e) => setUserMobile(e.target.value)}
              onSearch={() => setCurrentPage(1)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Search
              placeholder="نام سرویس یا توضیحات"
              value={textSearch}
              onChange={(e) => setTextSearch(e.target.value)}
              onSearch={() => setCurrentPage(1)}
              allowClear
            />
          </Col>

          <Col xs={12} sm={6} md={6} lg={4}>
            <Select
              allowClear
              placeholder="نوع سرویس"
              value={serviceType}
              onChange={(val) => {
                setServiceType(val || null);
                setCurrentPage(1);
              }}
              style={{ width: "100%" }}
            >
              {SERVICE_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={12} sm={6} md={6} lg={4}>
            <Select
              allowClear
              placeholder="وضعیت"
              value={status}
              onChange={(val) => {
                setStatus(val || null);
                setCurrentPage(1);
              }}
              style={{ width: "100%" }}
            >
              {SERVICE_STATUS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearAllFilters}
              style={{ width: "100%" }}
            >
              پاک کردن فیلترها
            </Button>
          </Col>
        </Row>

        {/* Collapsible Advanced Filters (Date Ranges & Expiry Window) */}
        <Collapse
          ghost
          style={{ marginTop: 10 }}
          expandIconPosition="end"
        >
          <Panel
            header={
              <Space>
                <FilterOutlined />
                <Text style={{ fontSize: 13, color: "#1890ff" }}>
                  فیلترهای پیشرفته تاریخ و انقضا
                </Text>
              </Space>
            }
            key="advanced"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  تاریخ خرید:
                </div>
                <RangePicker
                  placeholder={["از تاریخ", "تا تاریخ"]}
                  onChange={(d, strings) => {
                    setCreatedAtRange(parseDateRange(strings));
                    setCurrentPage(1);
                  }}
                  style={{ width: "100%" }}
                />
              </Col>

              <Col xs={24} md={8}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  تاریخ شروع:
                </div>
                <RangePicker
                  placeholder={["از تاریخ", "تا تاریخ"]}
                  onChange={(d, strings) => {
                    setStartAtRange(parseDateRange(strings));
                    setCurrentPage(1);
                  }}
                  style={{ width: "100%" }}
                />
              </Col>

              <Col xs={24} md={8}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  تاریخ پایان:
                </div>
                <RangePicker
                  placeholder={["از تاریخ", "تا تاریخ"]}
                  onChange={(d, strings) => {
                    setEndAtRange(parseDateRange(strings));
                    setCurrentPage(1);
                  }}
                  style={{ width: "100%" }}
                />
              </Col>

              <Col xs={24}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
                  انقضا در بازه تا X روز آتی:
                </div>
                <Space wrap>
                  <Radio.Group
                    value={expiryShortcut}
                    onChange={(e) => {
                      setExpiryShortcut(e.target.value);
                      setCurrentPage(1);
                    }}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="ALL">همه</Radio.Button>
                    <Radio.Button value="1">امروز (۱ روز)</Radio.Button>
                    <Radio.Button value="7">۷ روز آینده</Radio.Button>
                    <Radio.Button value="30">۳۰ روز آینده</Radio.Button>
                    <Radio.Button value="CUSTOM">سفارشی</Radio.Button>
                  </Radio.Group>

                  {expiryShortcut === "CUSTOM" && (
                    <InputNumber
                      min={1}
                      max={365}
                      placeholder="تعداد روز"
                      value={customExpiryDays}
                      onChange={(val) => {
                        setCustomExpiryDays(val);
                        setCurrentPage(1);
                      }}
                      style={{ width: 120 }}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </Card>

      {/* Table Section */}
      {loading && !data ? (
        <div style={{ backgroundColor: "#fff", padding: 24, borderRadius: 8 }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fff",
            padding: 20,
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <AdminServicesTable
            services={servicesList}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={10}
            loading={loading}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onOpenDetails={handleOpenDetails}
          />
        </div>
      )}

      {/* Details Drawer */}
      <AdminServiceTableDetails
        open={drawerOpen}
        onClose={handleCloseDetails}
        service={selectedService}
        windowSize={windowDimensions.innerWidth}
      />
    </div>
  );
}

export default AdminUserServices;
